import { OpenRouter } from "@openrouter/sdk";

export type ModelProvider = "openai" | "google" | "anthropic" | "openrouter";

export interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
}

export interface AIModel {
  id: string;
  name: string;
}

/**
 * Fetch available models from the provider API
 */
export const fetchAvailableModels = async (
  modelProvider: ModelProvider,
  apiKey: string,
): Promise<AIModel[]> => {
  if (!apiKey) {
    throw new Error("API key is required to fetch models");
  }

  try {
    if (modelProvider === "google") {
      return await fetchGoogleModels(apiKey);
    } else if (modelProvider === "openai") {
      return await fetchOpenAIModels(apiKey);
    } else if (modelProvider === "anthropic") {
      return await fetchAnthropicModels(apiKey);
    } else if (modelProvider === "openrouter") {
      return await fetchOpenRouterModels(apiKey);
    } else {
      throw new Error(`Unknown provider: ${modelProvider}`);
    }
  } catch (error) {
    throw new Error(
      `${error instanceof Error ? error.message : String(error)}`,
    );
  }
};

/**
 * Fetch Google Generative AI models
 */
const fetchGoogleModels = async (apiKey: string): Promise<AIModel[]> => {
  try {
    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models?key=${apiKey}`,
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Invalid Google API key");
      }
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = (await response.json()) as {
      models?: Array<{ name: string; displayName: string }>;
    };
    const models = data.models || [];

    return models
      .filter((model) => model.name.includes("gemini"))
      .map((model) => ({
        id: model.name.replace("models/", ""),
        name: model.displayName || model.name,
      }));
  } catch (error) {
    throw new Error(
      `${error instanceof Error ? error.message : String(error)}`,
    );
  }
};

/**
 * Fetch OpenAI models
 */
const fetchOpenAIModels = async (apiKey: string): Promise<AIModel[]> => {
  try {
    const response = await fetch("https://api.openai.com/v1/models", {
      headers: {
        Authorization: `Bearer ${apiKey}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Invalid OpenAI API key");
      }
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = (await response.json()) as {
      data?: Array<{ id: string; owned_by?: string }>;
    };
    const models = data.data || [];

    return models
      .filter((model) => model.id.includes("gpt"))
      .map((model) => ({
        id: model.id,
        name: `${model.id} (${model.owned_by || "OpenAI"})`,
      }));
  } catch (error) {
    throw new Error(
      `${error instanceof Error ? error.message : String(error)}`,
    );
  }
};

/**
 * Fetch Anthropic models
 */
const fetchAnthropicModels = async (apiKey: string): Promise<AIModel[]> => {
  try {
    const response = await fetch("https://api.anthropic.com/v1/models", {
      headers: {
        "x-api-key": apiKey,
        "anthropic-version": "2023-06-01",
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Invalid Anthropic API key");
      }
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = (await response.json()) as {
      data?: Array<{ id: string; display_name?: string; type?: string }>;
    };
    const models = data.data || [];

    // Filter for text models (exclude embedding and other types)
    return models
      .filter((model) => !model.type || model.type === "text")
      .map((model) => ({
        id: model.id,
        name: model.display_name || model.id,
      }));
  } catch (error) {
    throw new Error(
      `${error instanceof Error ? error.message : String(error)}`,
    );
  }
};

/**
 * Fetch OpenRouter models
 */
const fetchOpenRouterModels = async (apiKey: string): Promise<AIModel[]> => {
  try {
    const openrouter = new OpenRouter({ apiKey });
    const models = await openrouter.models.list();

    return (models.data || []).map((model: any) => ({
      id: model.id,
      name: model.name || model.id,
    }));
  } catch (error) {
    throw new Error(
      `${error instanceof Error ? error.message : String(error)}`,
    );
  }
};

/**
 * Test API connection with a lightweight endpoint (faster than fetching all models)
 */
export const testAPIConnection = async (
  modelProvider: ModelProvider,
  apiKey: string,
): Promise<boolean> => {
  try {
    if (modelProvider === "google") {
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models?key=${apiKey}&pageSize=1`,
      );
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return true;
    } else if (modelProvider === "openai") {
      const response = await fetch("https://api.openai.com/v1/models?limit=1", {
        headers: { Authorization: `Bearer ${apiKey}` },
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return true;
    } else if (modelProvider === "anthropic") {
      const response = await fetch("https://api.anthropic.com/v1/models?limit=1", {
        headers: {
          "x-api-key": apiKey,
          "anthropic-version": "2023-06-01",
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return true;
    } else if (modelProvider === "openrouter") {
      // Test OpenRouter with the SDK
      const openrouter = new OpenRouter({ apiKey });
      await openrouter.chat.send({
        model: "openrouter/auto",
        messages: [{ role: "user", content: "test" }],
        maxTokens: 1,
      });
      return true;
    }
    return false;
  } catch (error) {
    throw new Error(
      `API test failed: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
};

/**
 * Get the model instance and generate streaming response
 */
const generateResponseStream = async function* (
  modelProvider: ModelProvider,
  modelId: string,
  apiKey: string,
  messages: Message[],
  appName?: string,
  systemPrompt?: string,
): AsyncIterable<string> {
  if (modelProvider === "openai") {
    yield* generateOpenAIStream(modelId, apiKey, messages, systemPrompt);
  } else if (modelProvider === "google") {
    yield* generateGoogleStream(modelId, apiKey, messages, systemPrompt);
  } else if (modelProvider === "anthropic") {
    yield* generateAnthropicStream(modelId, apiKey, messages, systemPrompt);
  } else if (modelProvider === "openrouter") {
    yield* generateOpenRouterStream(modelId, apiKey, messages, appName, systemPrompt);
  } else {
    throw new Error(`Unsupported provider: ${modelProvider}`);
  }
};

/**
 * Generate response from OpenAI
 */
const generateOpenAIStream = async function* (
  modelId: string,
  apiKey: string,
  messages: Message[],
  systemPrompt?: string,
): AsyncIterable<string> {
  const apiMessages = [
    ...(systemPrompt ? [{ role: "system" as const, content: systemPrompt }] : []),
    ...messages.map((msg) => ({
      role: msg.role as "user" | "assistant",
      content: msg.content,
    })),
  ];

  const response = await fetch("https://api.openai.com/v1/chat/completions", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      model: modelId,
      messages: apiMessages,
      stream: true,
      temperature: 1,
    }),
  });

  if (!response.ok) {
    throw new Error(`OpenAI API error: ${response.status} ${response.statusText}`);
  }

  if (!response.body) {
    throw new Error("No response body");
  }

  yield* parseOpenAIStream(response.body);
};

/**
 * Parse server-sent events from OpenAI API
 */
const parseOpenAIStream = async function* (
  body: ReadableStream<Uint8Array>,
): AsyncIterable<string> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split("\n");

      for (let i = 0; i < events.length - 1; i++) {
        const event = events[i];
        if (event.startsWith("data: ")) {
          const data = event.slice(6);
          if (data === "[DONE]") {
            continue;
          }
          try {
            const parsed = JSON.parse(data);
            if (parsed.choices?.[0]?.delta?.content) {
              yield parsed.choices[0].delta.content;
            }
          } catch {
            // Skip invalid JSON events
          }
        }
      }

      buffer = events[events.length - 1];
    }
  } finally {
    reader.releaseLock();
  }
};

/**
 * Generate response from Google Generative AI
 */
const generateGoogleStream = async function* (
  modelId: string,
  apiKey: string,
  messages: Message[],
  systemPrompt?: string,
): AsyncIterable<string> {
  const systemMessage = systemPrompt
    ? [
        { role: "user", parts: [{ text: systemPrompt }] },
        { role: "model", parts: [{ text: "Understood. I will follow these instructions." }] },
      ]
    : [];

  const contents = [
    ...systemMessage,
    ...messages.map((msg) => ({
      role: msg.role === "user" ? "user" : "model",
      parts: [{ text: msg.content }],
    })),
  ];

  const response = await fetch(
    `https://generativelanguage.googleapis.com/v1beta/models/${modelId}:streamGenerateContent?alt=sse&key=${apiKey}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        contents,
        generationConfig: {
          temperature: 1,
          topK: 40,
          topP: 0.95,
        },
      }),
    },
  );

  if (!response.ok) {
    throw new Error(`Google API error: ${response.status} ${response.statusText}`);
  }

  if (!response.body) {
    throw new Error("No response body");
  }

  yield* parseGoogleStream(response.body);
};

/**
 * Parse server-sent events from Google API
 */
const parseGoogleStream = async function* (
  body: ReadableStream<Uint8Array>,
): AsyncIterable<string> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split("\n");

      for (let i = 0; i < events.length - 1; i++) {
        const event = events[i];
        if (event.startsWith("data: ")) {
          try {
            const data = JSON.parse(event.slice(6));
            if (data.candidates && data.candidates[0]?.content?.parts?.[0]?.text) {
              yield data.candidates[0].content.parts[0].text;
            }
          } catch {
            // Skip invalid JSON events
          }
        }
      }

      buffer = events[events.length - 1];
    }

    if (buffer.startsWith("data: ")) {
      try {
        const data = JSON.parse(buffer.slice(6));
        if (data.candidates && data.candidates[0]?.content?.parts?.[0]?.text) {
          yield data.candidates[0].content.parts[0].text;
        }
      } catch {
        // Skip invalid JSON events
      }
    }
  } finally {
    reader.releaseLock();
  }
};

/**
 * Generate response from Anthropic
 */
const generateAnthropicStream = async function* (
  modelId: string,
  apiKey: string,
  messages: Message[],
  systemPrompt?: string,
): AsyncIterable<string> {
  const anthropicMessages = messages.map((msg) => ({
    role: msg.role as "user" | "assistant",
    content: msg.content,
  }));

  const response = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": apiKey,
      "anthropic-version": "2023-06-01",
    },
    body: JSON.stringify({
      model: modelId,
      max_tokens: 1024,
      system: systemPrompt,
      messages: anthropicMessages,
      stream: true,
    }),
  });

  if (!response.ok) {
    throw new Error(`Anthropic API error: ${response.status} ${response.statusText}`);
  }

  if (!response.body) {
    throw new Error("No response body");
  }

  yield* parseAnthropicStream(response.body);
};

/**
 * Parse server-sent events from Anthropic API
 */
const parseAnthropicStream = async function* (
  body: ReadableStream<Uint8Array>,
): AsyncIterable<string> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split("\n");

      for (let i = 0; i < events.length - 1; i++) {
        const event = events[i];
        if (event.startsWith("data: ")) {
          try {
            const data = JSON.parse(event.slice(6));
            if (data.type === "content_block_delta" && data.delta?.type === "text_delta") {
              yield data.delta.text;
            }
          } catch {
            // Skip invalid JSON events
          }
        }
      }

      buffer = events[events.length - 1];
    }
  } finally {
    reader.releaseLock();
  }
};

/**
 * Generate response from OpenRouter
 */
const generateOpenRouterStream = async function* (
  modelId: string,
  apiKey: string,
  messages: Message[],
  appName?: string,
  systemPrompt?: string,
): AsyncIterable<string> {
  try {
    const apiMessages = [
      ...(systemPrompt ? [{ role: "system" as const, content: systemPrompt }] : []),
      ...messages.map((msg) => ({
        role: msg.role as "user" | "assistant",
        content: msg.content,
      })),
    ];

    const headers: HeadersInit = {
      "Authorization": `Bearer ${apiKey}`,
      "Content-Type": "application/json",
    };
    
    if (appName) {
      headers["X-Title"] = appName;
    }

    const response = await fetch("https://openrouter.ai/api/v1/chat/completions", {
      method: "POST",
      headers,
      body: JSON.stringify({
        model: modelId,
        messages: apiMessages,
        stream: true,
      }),
    });

    if (!response.ok) {
      if (response.status === 402) {
        throw new Error("API limit exhausted on OpenRouter. Please check your account plan or wait for the limit to reset.");
      }
      const error = await response.text();
      throw new Error(`HTTP ${response.status}: ${error}`);
    }

    if (!response.body) {
      throw new Error("No response body");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6);
            if (data === "[DONE]") {
              return;
            }
            try {
              const parsed = JSON.parse(data);
              const content = parsed.choices?.[0]?.delta?.content;
              if (content) {
                yield content;
              }
            } catch {
              // Ignore parse errors
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  } catch (error) {
    throw new Error(
      `${error instanceof Error ? error.message : String(error)}`,
    );
  }
};

/**
 * Generate a streaming chat response from the AI
 * Returns an async iterator that yields text chunks
 */
export const generateChatResponse = (
  messages: Message[],
  modelProvider: ModelProvider,
  modelId: string,
  apiKey: string,
  appName?: string,
  systemPrompt?: string,
): AsyncIterable<string> => {
  if (!apiKey || !modelId) {
    throw new Error("API key and model ID are required");
  }

  // Return an async iterable that streams the response
  return {
    async *[Symbol.asyncIterator]() {
      try {
        yield* generateResponseStream(modelProvider, modelId, apiKey, messages, appName, systemPrompt);
      } catch (error) {
        throw new Error(
          `${error instanceof Error ? error.message : String(error)}`,
        );
      }
    },
  };
};

/**
 * Validate AI configuration
 */
export const validateAIConfig = (
  modelProvider: ModelProvider | "",
  modelId: string,
  apiKey: string,
): { valid: boolean; error?: string } => {
  if (!apiKey) {
    return { valid: false, error: "Please enter your API key" };
  }
  if (!modelProvider) {
    return { valid: false, error: "Please select a model provider" };
  }
  if (!modelId) {
    return { valid: false, error: "Please enter a model ID" };
  }
  return { valid: true };
};
